import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeSlash,
  Phone,
  Calendar,
  User,
  Plus,
  WhatsappLogo
} from '@phosphor-icons/react';
import CustomClinikitActionButton from '../ui/CustomClinikitActionButton';
import EmptyDraw from '../ui/EmptyDraw';
import './PatientsCards.css';
import ContentLoader from '../shared/ContentLoader';

const STATUS_CONFIG = {
  active: { label: 'פעיל', color: 'green' },
  waiting: { label: 'בהמתנה', color: 'yellow' },
  finished: { label: 'סיים טיפול', color: 'gray' },
  initial: { label: 'פנייה ראשונית', color: 'blue' },
};

const getPhoneLinks = (phone) => {
  if (!phone) return null;
  const digits = phone.replace(/[^\d+]/g, "");
  const waDigits = digits.replace(/^\+/, "").replace(/^0/, "972").replace(/^9720/, "972");
  return { tel: `tel:${digits}`, whatsapp: `https://wa.me/${waDigits}` };
};

function PatientsCards({ 
  patients, 
  currentPage, 
  totalPages, 
  onPageChange, 
  isLoading,
  onCardClick: onCardClickProp,
  showTherapist = false,
  isAdminView = false,
  patientTypes = [],
  hasFilters = false,
  onAddPatient
}) {
  const navigate = useNavigate();
  const [visibleNotes, setVisibleNotes] = useState({});

  const toggleNoteVisibility = (patientId, e) => {
    e.stopPropagation();
    setVisibleNotes(prev => ({
      ...prev,
      [patientId]: !prev[patientId]
    }));
  };

  // Feed cursor position to the CSS glow (--mx/--my on the hovered card)
  const handleCardMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  const handleCardClick = (patientId) => {
    if (onCardClickProp) {
      onCardClickProp(patientId);
    } else {
      navigate(`/dashboard/patients/${patientId}`);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('he-IL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return `${first}${last}`;
  };

  // Get patient type badges for a patient
  const getPatientTypeBadges = (patientTypeIds) => {
    if (!patientTypeIds?.length || !patientTypes?.length) return null;
    return patientTypeIds
      .map(id => patientTypes.find(t => t.id === id))
      .filter(Boolean)
      .slice(0, 2); // Show max 2 badges
  };

  if (isLoading) {
    return (
      <ContentLoader fill message="טוען מטופלים..." />
    );
  }

  if (!patients || patients.length === 0) {
    return (
      <div className="patients-empty-state">
        <EmptyDraw variant={hasFilters ? "search" : "patients"} />
        <h3 className="empty-state-title">
          {hasFilters ? 'לא נמצאו תוצאות' : 'עוד אין מטופלים'}
        </h3>
        <p className="empty-state-desc">
          {hasFilters 
            ? 'נסה לשנות את מילות החיפוש או הסינון' 
            : 'הוסף את המטופל הראשון שלך והתחל לנהל את היומן'}
        </p>
        {!hasFilters && onAddPatient && (
          <CustomClinikitActionButton
            icon={<Plus size={16} weight="bold" />}
            onClick={onAddPatient}
          >
            הוסף מטופל ראשון
          </CustomClinikitActionButton>
        )}
      </div>
    );
  }

  return (
    <div className="patients-cards-container">
      <div
        className="patients-cards-grid"
        key={patients.map((p) => p.id).join("|")}
      >
        {patients.map((patient) => {
          const status = STATUS_CONFIG[patient.status] || STATUS_CONFIG.active;
          const hasNotes = patient.adminNotes && patient.adminNotes.trim();
          const isNoteVisible = visibleNotes[patient.id];
          const fullName = `${patient.firstName} ${patient.lastName}`;
          const phoneLinks = getPhoneLinks(patient.phone);

          return (
            <div
              key={patient.id}
              className={`patient-card ${isAdminView ? 'admin-view' : ''}`}
              onClick={() => handleCardClick(patient.id)}
              onMouseMove={handleCardMouseMove}
            >
              <div className="card-header">
                <div className="patient-avatar">
                  {getInitials(patient.firstName, patient.lastName)}
                </div>
                {isAdminView && (
                  <div className="admin-badge">
                    <Eye size={12} weight="bold" />
                    <span>צפייה</span>
                  </div>
                )}
              </div>

              <div className="card-body">
                <h3 className="patient-name">{fullName}</h3>
                <div className="card-badges-row">
                  {patient.isActive === false ? (
                    <span className="status-badge status-inactive">
                      לא פעיל
                    </span>
                  ) : (
                    <span className={`status-badge status-${status.color}`}>
                      {status.label}
                    </span>
                  )}
                  {getPatientTypeBadges(patient.patientTypeIds)?.length > 0 && (
                    <div className="patient-type-badges">
                      {getPatientTypeBadges(patient.patientTypeIds).map(type => (
                        <span
                          key={type.id}
                          className="patient-type-mini-badge"
                          style={{ backgroundColor: type.color }}
                          title={type.label}
                        >
                          {type.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="card-info">
                  {showTherapist && patient.therapistName && (
                    <div className="info-row therapist-row">
                      <User size={16} weight="regular" />
                      <span>{patient.therapistName}</span>
                    </div>
                  )}
                  {patient.phone && (
                    <div className="info-row">
                      <Phone size={16} weight="regular" />
                      <span>{patient.phone}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <Calendar size={16} weight="regular" />
                    <span>
                      {patient.lastAppointmentDate 
                        ? formatDate(patient.lastAppointmentDate)
                        : 'אין פגישות'}
                    </span>
                  </div>
                </div>

                {phoneLinks && (
                  <div className="card-quick-actions">
                    <a
                      href={phoneLinks.tel}
                      className="quick-action-btn quick-action-call"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`חיוג אל ${fullName}`}
                      title="חיוג"
                    >
                      <Phone size={17} weight="regular" />
                    </a>
                    <a
                      href={phoneLinks.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="quick-action-btn quick-action-whatsapp"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`וואטסאפ אל ${fullName}`}
                      title="שליחת וואטסאפ"
                    >
                      <WhatsappLogo size={17} weight="regular" />
                    </a>
                  </div>
                )}

                {hasNotes && (
                  <div className="card-notes">
                    <button
                      className="notes-toggle"
                      onClick={(e) => toggleNoteVisibility(patient.id, e)}
                    >
                      {isNoteVisible ? (
                        <EyeSlash size={16} weight="regular" />
                      ) : (
                        <Eye size={16} weight="regular" />
                      )}
                      <span>{isNoteVisible ? 'הסתר הערה' : 'הצג הערה'}</span>
                    </button>
                    {isNoteVisible && (
                      <p className="notes-text">{patient.adminNotes}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          <span className="pagination-info">
            עמוד {currentPage} מתוך {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

export default PatientsCards;

